import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../context/notificationContext";
import { useUserSettings } from "../../../context/userSettingsContext";
import useDataInitializer from "../../../hooks/useDataInitializer";
import { useDebounce } from "../../../hooks/useDebounce";
import useListFilters from "../../../hooks/useListFilters";
import { useI18n } from "../../../hooks/useTranslations";
import { getCategories } from "../../../http/cars/types";
import { useCostImportsApi } from "../../../http/costImports/api";
import { useDkvCostImportsApi } from "../../../http/dkvCostImports/api";
import { DkvCostImport } from "../../../http/dkvCostImports/types";
import { PaginationQuery } from "../../../types";
import { downloadFileFromData, formatCurrency, formatNumber, formatTimeStampNumeric } from "../../../types/utils";
import CostTypes from "../../costs/types/CostTypes";
import SvgInfo from "../../icons/Info";
import { ErrorBox } from "../../ui/Boxes";
import Button from "../../ui/Button";
import CustomLink from "../../ui/CustomLink";
import ExportButton from "../../ui/ExportButton";
import Icon from "../../ui/Icon";
import Table from "../../ui/Table";
import Tooltip from "../../ui/Tooltip";
import TextColumnFilter from "../../ui/columnFilters/TextColumnFilter";
import CarAssignmentCell from "../CarAssignmentCell";
import CarAssignmentDialog from "../CarAssignmentDialog";
import styles from "./DkvCostImport.module.css";
import ImportDkvCostImportButton from "./ImportDkvCostImportButton";

interface DkvCostImportsProps {
  withErrors?: boolean;
  notExported?: boolean;
  forced?: boolean;
}

const DkvCostImports: FC<DkvCostImportsProps> = ({ withErrors, notExported, forced }: DkvCostImportsProps) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const dkvImportsApi = useDkvCostImportsApi();
  const costImportsApi = useCostImportsApi();
  const filters = useListFilters({
    isSearchable: true,
    prefix:
      withErrors === true
        ? "dkv_err"
        : withErrors === false
          ? "dkv_no_err"
          : notExported
            ? "dkv_not_exp"
            : forced
              ? "dkv_forced"
              : undefined,
  });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);
  const { id } = useParams<{ id: string }>();
  const [showCostTypes, setShowCostTypes] = useState(false);
  const [assignmentItem, setAssignmentItem] = useState<DkvCostImport | null>(null);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const categories = getCategories(i18n.t);

  const { data: costImport, trigger } = costImportsApi.getCostImport(id!, aborter.current.signal);

  const result = dkvImportsApi.useDkvCostImports(query, aborter.current.signal, id!, withErrors, notExported, forced);
  const [data] = useDataInitializer(result.data, result.error);

  const dataWithErrors = dkvImportsApi.useDkvCostImports({}, aborter.current.signal, id!, true);

  const subsidiary = costImport?.subsidiary;
  const getSubsidiaryEditUrl = (subsidiaryId: string, tab?: number) => {
    const params = new URLSearchParams();
    tab !== undefined && params.set("tab", String(tab));
    params.set("redirect_uri", window.location.pathname + window.location.search);
    return `/admin/subsidiaries/${subsidiaryId}?${params.toString()}`;
  };
  const missingSubsidiaryExportSettings = !subsidiary
    ? []
    : [
        !subsidiary.vatServiceCode && i18n.t.subsidiaries.errors.missingVatServiceCode,
        !subsidiary.countryIsoCodeAlpha2 && i18n.t.subsidiaries.errors.missingCountryIsoCode,
        !subsidiary.vatDeductionCode && i18n.t.subsidiaries.errors.missingVatDeductionCode,
      ].filter((message): message is string => !!message);

  useEffect(() => {
    (result.error || dataWithErrors.error) &&
      dispatch(showErrorNotification(i18n, result.error || dataWithErrors.error));
  }, [result.error, dataWithErrors.error]);

  useEffect(() => {
    !!id && trigger();
  }, [id]);

  const filterOptions = {
    get: dkvImportsApi.getDkvFieldFilters,
    update: filters.setFilters,
    getSelected: filters.getFilters,
  };

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  const reloadData = async () => {
    try {
      await dkvImportsApi.reloadDkvLines(id!);
      dispatch(showSuccessNotification(i18n.t, i18n.t.common.dataHasBeenUpdatedSuccesfully));
    } catch (error) {
      dispatch(showErrorNotification(i18n, error));
    }
  };

  const getRowMeta = (item: DkvCostImport) => {
    const formattedDate = formatTimeStampNumeric(item.date, i18n.language);
    const assignedUser = item.assignment?.carDriver?.driver;
    const assignment = assignedUser?.fullName || item.assignment?.otherCarAssignment?.type || null;
    const missingCostCenterMessage = assignment
      ? i18n.t.costsImport.dkv.noCostCenterFound(assignment, formattedDate)
      : i18n.t.costsImport.dkv.noUserCostCenterFound(item.carPlateNumber, formattedDate);

    // only pkw, pkw-superior, lkw are exported
    const isToExport = !item.car || (!!item.car?.category && item.car.category !== "pkw-up");
    const hasMissingDriver = !assignedUser && !assignment;

    return { formattedDate, assignedUser, assignment, missingCostCenterMessage, isToExport, hasMissingDriver };
  };

  const renderAssignmentDialog = (item: DkvCostImport) => {
    const { formattedDate, assignment } = getRowMeta(item);

    return (
      <CarAssignmentDialog
        date={item.date}
        formattedDate={formattedDate}
        originalPlate={item.carPlateNumber}
        carId={item.car?.id}
        plateHint={item.car ? undefined : item.carPlateNumber}
        forcedByUser={item.carForcedByUser}
        forcedAt={item.carForcedAt}
        assignmentForcedByUser={item.assignmentForcedByUser}
        assignmentForcedAt={item.assignmentForcedAt}
        forcedAssignmentId={item.assignment?.carDriver?.id ?? item.assignment?.otherCarAssignment?.id}
        forcedAssignmentName={assignment ?? undefined}
        onSave={async (car) => {
          try {
            await dkvImportsApi.forceCar(item.id, car.id, id!);
            dispatch(showSuccessNotification(i18n.t, i18n.t.common.dataHasBeenUpdatedSuccesfully));
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
          }
        }}
        onRemoveForce={async () => {
          try {
            await dkvImportsApi.removeForcedCar(item.id, id!);
            dispatch(showSuccessNotification(i18n.t, i18n.t.common.dataHasBeenUpdatedSuccesfully));
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
          }
        }}
        onForceAssignment={async (carId, assignmentId, assignmentType) => {
          try {
            await dkvImportsApi.forceAssignment(item.id, carId, assignmentId, assignmentType, id!);
            dispatch(showSuccessNotification(i18n.t, i18n.t.common.dataHasBeenUpdatedSuccesfully));
            return true;
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
            return false;
          }
        }}
        onAssignDriver={async (carId) => {
          try {
            await dkvImportsApi.removeForcedAssignment(item.id, carId, id!);
            dispatch(showSuccessNotification(i18n.t, i18n.t.common.dataHasBeenUpdatedSuccesfully));
            return true;
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
            return false;
          }
        }}
        onClose={() => setAssignmentItem(null)}
      />
    );
  };

  return (
    <>
      {missingSubsidiaryExportSettings.length > 0 && (
        <ErrorBox
          text={
            <>
              {missingSubsidiaryExportSettings.join(" / ")}
              {!!subsidiary && <CustomLink to={getSubsidiaryEditUrl(subsidiary.id)}>{i18n.t.common.edit}</CustomLink>}
            </>
          }
          className={styles.subsidiaryExportWarning}
        />
      )}
      <Table<DkvCostImport>
        filters={filters}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        buttons={{
          secondary: userSettings.permissions.costsImports.insert
            ? [
                <Button
                  buttonProps={{
                    onClick: () => reloadData(),
                    disabled: !!costImport?.isImportedInCosts,
                  }}
                >
                  {i18n.t.common.reloadData}
                </Button>,
                userSettings.permissions.costTypes.read && (
                  <Button
                    buttonProps={{
                      onClick: () => setShowCostTypes(true),
                    }}
                  >
                    {i18n.t.common.manageCostTypes}
                  </Button>
                ),
                !!dataWithErrors && dataWithErrors.data?.total == 0 && (
                  <ImportDkvCostImportButton
                    isDisabled={!costImport || costImport?.isImportedInCosts}
                    onUpdate={trigger}
                  />
                ),
                !!dataWithErrors &&
                  dataWithErrors.data?.total == 0 &&
                  (() => {
                    const exportButton = (
                      <ExportButton.Excel
                        disabled={missingSubsidiaryExportSettings.length > 0}
                        onClick={async () => {
                          try {
                            const r = await dkvImportsApi.exportExcel(id!, aborter.current.signal);
                            downloadFileFromData(r.data, "export", r.contentType);
                          } catch (error) {
                            dispatch(showErrorNotification(i18n, error));
                          }
                        }}
                      />
                    );

                    return missingSubsidiaryExportSettings.length > 0 ? (
                      <Tooltip
                        message={missingSubsidiaryExportSettings.join(" / ")}
                        to={subsidiary ? getSubsidiaryEditUrl(subsidiary.id) : undefined}
                        isError
                      >
                        {exportButton}
                      </Tooltip>
                    ) : (
                      exportButton
                    );
                  })(),
              ]
            : [],
        }}
        head={
          <>
            <Table.Th
              title={i18n.t.common.country}
              fieldName="dkv.country"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th
              title={i18n.t.cars.singular}
              fieldName="dkv.car.plateNumber"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            {forced && <Table.Th title={i18n.t.common.forcedBy} fieldName="dkv.carForcedBy" />}
            <Table.Th
              title={i18n.t.common.carCategory}
              fieldName="dkv.car.category"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
              formatter={(value) => categories.find((x) => x.value == value)?.label}
            />
            <Table.Th
              title={i18n.t.drivers.singular}
              fieldName="dkv.driver.name"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th title={i18n.t.common.date} fieldName="dkv.date" order={orderOptions} />
            <Table.Th title={i18n.t.common.quantity} fieldName="dkv.quantity" order={orderOptions} />
            <Table.Th
              title={i18n.t.common.unit}
              fieldName="dkv.unit"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th align="right" title={i18n.t.common.costNet} fieldName="dkv.cost.net" order={orderOptions} />
            <Table.Th align="right" title={i18n.t.common.costGross} fieldName="dkv.cost.gross" order={orderOptions} />

            <Table.Th align="right" title={i18n.t.common.vat} fieldName="dkv.vat" order={orderOptions} />
            <Table.Th
              align="right"
              title={i18n.t.common.discountNet}
              fieldName="dkv.discount.net"
              order={orderOptions}
            />
            <Table.Th
              align="right"
              title={i18n.t.common.discountGross}
              fieldName="dkv.discount.gross"
              order={orderOptions}
            />
            <Table.Th
              title={i18n.t.common.productType}
              fieldName="dkv.product.type"
              order={orderOptions}
              filters={filterOptions}
              filter={TextColumnFilter}
            />
            <Table.Th
              title={i18n.t.common.productCategory}
              fieldName="dkv.product.category"
              order={orderOptions}
              filters={filterOptions}
              filter={TextColumnFilter}
            />
            <Table.Th
              title={i18n.t.common.productCode}
              fieldName="dkv.product.code"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th
              title={i18n.t.common.costCenter}
              fieldName="dkv.cost.center"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th
              title={i18n.t.common.costObject}
              fieldName="dkv.costType.costObject"
              filters={filterOptions}
              filter={TextColumnFilter}
              order={orderOptions}
            />
            <Table.Th
              align="right"
              title={i18n.t.common.serviceFeeNet}
              fieldName="dkv.serviceFee.net"
              order={orderOptions}
            />
          </>
        }
        renderRow={(item) => {
          const { formattedDate, assignedUser, assignment, missingCostCenterMessage, isToExport, hasMissingDriver } =
            getRowMeta(item);
          const subsidiaryId = costImport?.subsidiary?.id;

          const excludedMessage = i18n.t.costsImport.excludedFromExport(item.carPlateNumber);
          const forcedMessage = item.carForcedAt
            ? i18n.t.costsImport.carForcedManually(formatTimeStampNumeric(item.carForcedAt, i18n.language))
            : item.assignmentForcedAt
              ? i18n.t.costsImport.assignmentForcedManually(
                  formatTimeStampNumeric(item.assignmentForcedAt, i18n.language),
                )
              : undefined;
          const wrapPlain = (content: React.ReactNode) =>
            !isToExport && !notExported ? (
              <Tooltip message={excludedMessage} children={content} />
            ) : forcedMessage ? (
              <Tooltip message={forcedMessage} children={content} />
            ) : (
              content
            );

          const carCellContent = item.car ? (
            forcedMessage ? (
              <Tooltip
                message={forcedMessage}
                children={
                  <CustomLink to={`/admin/cars/${item.car.id}`}>
                    {item.car.currentPlateNumber || item.car.lastPlateNumber}
                  </CustomLink>
                }
              />
            ) : (
              <CustomLink to={`/admin/cars/${item.car.id}`}>
                {item.car.currentPlateNumber || item.car.lastPlateNumber}
              </CustomLink>
            )
          ) : (
            <Tooltip
              message={i18n.t.costsImport.dkv.noCarFound(item.carPlateNumber, formattedDate)}
              children={item.carPlateNumber}
              isError
            />
          );

          return (
            <>
              <td>{wrapPlain(item.serviceCountryCode)}</td>
              <td>
                {isToExport ? (
                  <CarAssignmentCell
                    canEdit={
                      hasMissingDriver ||
                      (!!item.carForcedByUser && !!item.carForcedAt) ||
                      (!!item.assignmentForcedByUser && !!item.assignmentForcedAt)
                    }
                    onEdit={() => setAssignmentItem(item)}
                  >
                    {carCellContent}
                  </CarAssignmentCell>
                ) : (
                  wrapPlain(item.carPlateNumber)
                )}
              </td>
              {forced && <td>{item.carForcedByUser?.fullName ?? item.assignmentForcedByUser?.fullName}</td>}
              <td>{wrapPlain(categories.find((x) => x.value == item.car?.category)?.label)}</td>
              <td>
                {isToExport ? (
                  !item.costCenter ? (
                    assignedUser ? (
                      <Tooltip
                        message={missingCostCenterMessage}
                        to={`/admin/users/${assignedUser.id}`}
                        children={assignedUser.fullName}
                        isError
                      />
                    ) : assignment ? (
                      <Tooltip message={missingCostCenterMessage} children={assignment} isError />
                    ) : (
                      <Tooltip
                        message={i18n.t.costsImport.dkv.noDriverFound(item.carPlateNumber, formattedDate)}
                        isError
                      />
                    )
                  ) : assignedUser ? (
                    wrapPlain(<CustomLink to={`/admin/users/${assignedUser.id}`}>{assignedUser.fullName}</CustomLink>)
                  ) : assignment ? (
                    wrapPlain(assignment)
                  ) : (
                    <Tooltip
                      message={i18n.t.costsImport.dkv.noDriverFound(item.carPlateNumber, formattedDate)}
                      isError
                    />
                  )
                ) : (
                  wrapPlain(assignedUser?.fullName || assignment || null)
                )}
              </td>
              <Table.Td noWrap>{wrapPlain(formattedDate)}</Table.Td>
              <td>
                {wrapPlain(
                  item.quantity
                    ? formatNumber(item.quantity, i18n.language, {
                        fractionalDigits: 2,
                      })
                    : null,
                )}
              </td>
              <td>{wrapPlain(item.unit)}</td>

              <Table.Td align="right" noWrap>
                {wrapPlain(formatCurrency(item.costNet, i18n.language, item.currency))}
              </Table.Td>
              <Table.Td align="right" noWrap>
                {wrapPlain(formatCurrency(item.costGross, i18n.language, item.currency))}
              </Table.Td>
              <Table.Td align="right" noWrap>
                {wrapPlain(formatCurrency(item.vat, i18n.language, item.currency))}
              </Table.Td>
              <Table.Td align="right" noWrap>
                {wrapPlain(formatCurrency(item.discountNet, i18n.language, item.currency))}
              </Table.Td>
              <Table.Td align="right" noWrap>
                {wrapPlain(formatCurrency(item.discountGross, i18n.language, item.currency))}
              </Table.Td>
              <td>{wrapPlain(item.productType)}</td>
              <td>{wrapPlain(item.productCategory)}</td>
              <td>
                {isToExport ? (
                  !item.subsidiaryVatCodeId ? (
                    <Tooltip
                      message={i18n.t.costsImport.dkv.noSubsidiaryVatCodeFound(item.productCode)}
                      to={subsidiaryId ? getSubsidiaryEditUrl(subsidiaryId, 1) : undefined}
                      children={
                        <span className={styles.inlineInfo}>
                          {item.productCode}
                          <Icon glyph={SvgInfo} className={styles.inlineInfoIcon} />
                        </span>
                      }
                      isError
                    />
                  ) : !item.costType ? (
                    <Tooltip
                      message={i18n.t.costsImport.dkv.noCostTypeFound(item.productCode, item.productCategory)}
                      children={
                        <span className={styles.inlineInfo}>
                          {item.productCode}
                          <Icon glyph={SvgInfo} className={styles.inlineInfoIcon} />
                        </span>
                      }
                      isError
                    />
                  ) : (
                    wrapPlain(item.productCode)
                  )
                ) : (
                  wrapPlain(item.productCode)
                )}
              </td>
              <td>
                {isToExport ? (
                  !item.costCenter ? (
                    <Tooltip message={missingCostCenterMessage} isError />
                  ) : (
                    wrapPlain(item.costCenter)
                  )
                ) : (
                  wrapPlain(item.costCenter)
                )}
              </td>
              <td>{wrapPlain(item.costType?.costObject)}</td>
              <Table.Td align="right" noWrap>
                {wrapPlain(formatCurrency(item.serviceFeeNet, i18n.language, item.currency))}
              </Table.Td>
            </>
          );
        }}
        getRowStyle={(item) => {
          if (!item.car) {
            return { "--row-bg": "var(--red-20)", "--row-hover-bg": "var(--red-40)" } as React.CSSProperties;
          }
          if (item.carForcedAt || item.assignmentForcedAt) {
            return { "--row-bg": "var(--blue-20)", "--row-hover-bg": "var(--blue-40)" } as React.CSSProperties;
          }
          if (item.car?.category == undefined || item.car?.category == "pkw-up") {
            return { "--row-bg": "var(--yellow-20)", "--row-hover-bg": "var(--yellow-40)" } as React.CSSProperties;
          }
          return undefined;
        }}
        error={result.error}
        items={data?.items}
        total={data?.total}
      />

      {assignmentItem &&
        renderAssignmentDialog(data?.items.find((item) => item.id === assignmentItem.id) ?? assignmentItem)}

      {showCostTypes && <CostTypes onClose={() => setShowCostTypes(false)} />}
    </>
  );
};

export default DkvCostImports;
